import React from "react";
import ProductUpdatesCodexSettings from "./ProductUpdatesCodexSettings.jsx";

const IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);

async function clipboardImage(blob) {
  if (!IMAGE_TYPES.has(blob.type)) throw new Error("Copiez une image PNG, JPEG ou WebP.");
  if (blob.size > 8 * 1024 * 1024) throw new Error("L'image dépasse 8 Mo. Copiez une image plus petite.");
  const bitmap = await createImageBitmap(blob).catch(() => { throw new Error("L'image copiée est illisible."); });
  try {
    if (bitmap.width * bitmap.height > 16000000) throw new Error("Les dimensions de l'image sont trop grandes.");
    const scale = Math.min(1, 1280 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return { data_url: canvas.toDataURL("image/webp", .86), alt: "Illustration des nouveautés du Journal" };
  } finally { bitmap.close(); }
}

export default function ProductUpdateImageEditor({ image, onChange, onGenerate, notices = [], disabled = false, pending = false, codexLocalAvailable = false, engines = ["codex"], allowPerItem = false, autoSave = false, imageLabel, instructions: savedInstructions, onInstructionsChange }) {
  const [options, setOptions] = React.useState({ ready: false });
  const codexReady = options.ready;
  // autoSave: the parent saves the unsaved changes before generating, so they do not block the button.
  const blocking = pending && !autoSave;
  const localName = options.engine === "claude" ? "Claude" : "Codex";
  // A draft keeps the instructions in its saved state; elsewhere they live in this editor.
  const [ownInstructions, setOwnInstructions] = React.useState({});
  const instructions = savedInstructions || ownInstructions;
  const setInstructions = onInstructionsChange || setOwnInstructions;
  const [mode, setMode] = React.useState(image?.additional_images?.length || instructions["2"]?.trim() ? "two" : "common");
  const twoImages = mode === "two";
  const perItem = allowPerItem && mode === "per_item";
  const [error, setError] = React.useState("");
  const [marking, setMarking] = React.useState(false);
  const zoomRef = React.useRef(null);
  const [beforeMark, setBeforeMark] = React.useState(null);
  const [imageSelection, setImageSelection] = React.useState(null);
  const [imageSource, setImageSource] = React.useState(codexLocalAvailable ? "saas_capture" : "illustration");
  const imageItemIds = notices.filter(item => imageSelection === null || imageSelection.includes(item.item_id)).map(item => item.item_id);
  // Required instructions, one per image: they alone describe its content.
  const instructionFields = perItem
    ? notices.filter(item => imageItemIds.includes(item.item_id)).map(item => ({ key: item.item_id, item_id: item.item_id, label: `Consignes de l’image « ${item.title} »` }))
    : twoImages ? [{ key: "1", label: "Consignes de l’image 1" }, { key: "2", label: "Consignes de l’image 2" }] : [{ key: "1", label: "Consignes de l’image" }];
  const imageSpecs = instructionFields.map(({ key, item_id }) => ({ instructions: (instructions[key] || "").trim(), ...(item_id ? { item_id } : {}) }));
  const expectedImage = React.useRef(image?.data_url);
  const [reading, setReading] = React.useState(false);
  const readingRef = React.useRef(false);
  const zoneRef = React.useRef(null);
  const helpId = React.useId();
  const instructionsHelpId = React.useId();
  React.useEffect(() => {
    if (image?.data_url !== expectedImage.current) { setBeforeMark(null); setMarking(false); }
    expectedImage.current = image?.data_url;
  }, [image?.data_url]);

  function replaceImage(nextImage) {
    if (image?.additional_images?.length) {
      nextImage = nextImage ? { ...nextImage, ...(Object.hasOwn(nextImage, "additional_images") ? {} : { additional_images: image.additional_images }) }
        : { ...image.additional_images[0], ...(image.item_ids ? { item_ids: image.item_ids } : {}) };
    }
    expectedImage.current = nextImage?.data_url;
    setBeforeMark(null); setMarking(false); setError(""); onChange(nextImage);
  }

  function markCapture(event) {
    const picture = event.currentTarget.querySelector("img");
    if (!picture?.naturalWidth || disabled || reading) return;
    const rect = picture.getBoundingClientRect();
    const scale = Math.min(rect.width / picture.naturalWidth, rect.height / picture.naturalHeight);
    const left = rect.left + (rect.width - picture.naturalWidth * scale) / 2;
    const top = rect.top + (rect.height - picture.naturalHeight * scale) / 2;
    let x = event.detail ? (event.clientX - left) / scale : picture.naturalWidth / 2;
    let y = event.detail ? (event.clientY - top) / scale : picture.naturalHeight / 2;
    if (x < 0 || y < 0 || x > picture.naturalWidth || y > picture.naturalHeight) return;
    const canvas = document.createElement("canvas"); canvas.width = picture.naturalWidth; canvas.height = picture.naturalHeight;
    const context = canvas.getContext("2d"); context.drawImage(picture, 0, 0);
    const radius = Math.min(canvas.width, canvas.height) * .12;
    x = Math.max(radius, Math.min(canvas.width - radius, x)); y = Math.max(radius, Math.min(canvas.height - radius, y));
    context.lineCap = "round"; context.beginPath();
    context.moveTo(x - radius, y - radius); context.lineTo(x + radius, y + radius);
    context.moveTo(x + radius, y - radius); context.lineTo(x - radius, y + radius);
    context.strokeStyle = "white"; context.lineWidth = Math.max(5, radius * .25); context.stroke();
    context.strokeStyle = getComputedStyle(zoneRef.current).getPropertyValue("--color-red-600").trim();
    context.lineWidth = Math.max(3, radius * .16); context.stroke();
    const next = { ...image, data_url: canvas.toDataURL("image/webp", .9) };
    expectedImage.current = next.data_url; setBeforeMark(current => current || image);
    setMarking(false); onChange(next);
  }

  // With two common images, each one has its own button and the other image is kept.
  const specsFor = index => index ? [imageSpecs[index - 1]] : imageSpecs;
  const reasonFor = (index, local = true) => disabled || reading ? "Action en cours…" : blocking ? "Enregistrez d’abord vos changements."
    : !imageItemIds.length ? "Cochez au moins une nouveauté à illustrer." : specsFor(index).some(spec => !spec.instructions) ? "Saisissez les consignes de chaque image."
    : index === 2 && !image ? "Créez d’abord l’image 1." : local && !codexReady ? "Choisissez un modèle disponible ou attendez son chargement." : "";
  const imageButtons = twoImages ? [1, 2] : [0];

  function generate(generator, index) {
    if (reasonFor(index, generator === "codex_local")) return;
    setBeforeMark(null); setMarking(false); setError(""); onGenerate(generator, imageItemIds, imageSource, options, perItem ? "per_item" : "common", specsFor(index), index);
  }

  async function readImage(readBlob) {
    if (disabled || readingRef.current) return;
    readingRef.current = true; setReading(true); setError("");
    try {
      const blob = await readBlob();
      replaceImage(await clipboardImage(blob));
    } catch (failure) {
      setError(failure.name === "NotAllowedError" || failure.name === "SecurityError"
        ? "Cliquez dans cette zone et appuyez sur Ctrl+V pour coller l'image copiée."
        : failure.message || "Impossible de lire l'image copiée. Essayez Ctrl+V dans cette zone.");
    } finally { readingRef.current = false; setReading(false); }
  }

  function paste(event) {
    event.stopPropagation();
    if (disabled || readingRef.current) return;
    const file = [...(event.clipboardData?.files || [])].find(entry => entry.type.startsWith("image/"));
    event.preventDefault();
    if (!file) { setError("Copiez l'image elle-même, puis collez-la ici avec Ctrl+V."); return; }
    void readImage(async () => file);
  }
  function readClipboard() {
    if (!navigator.clipboard?.read) {
      zoneRef.current?.focus(); setError("Appuyez sur Ctrl+V dans cette zone pour coller l'image copiée."); return;
    }
    void readImage(async () => {
      const entries = await navigator.clipboard.read();
      for (const entry of entries) {
        const type = entry.types.find(value => IMAGE_TYPES.has(value));
        if (type) return entry.getType(type);
      }
      throw new Error("Le presse-papiers ne contient pas d'image. Copiez l'image elle-même, puis réessayez.");
    });
  }

  const label = imageLabel || (allowPerItem ? "Images de la publication" : "Image du Journal");
  return <div ref={zoneRef} className="pu-image-editor" role="group" aria-label={label}
    aria-describedby={helpId} aria-disabled={disabled || reading} tabIndex={disabled ? -1 : 0} onPaste={paste}>
    <strong>{label}</strong>
    <p id={helpId}>Privilégiez une capture réelle de la fonction. Cliquez dans cette zone et collez l'image avec Ctrl+V pour l'ajouter ou la remplacer.</p>
    {image?.additional_images?.length > 0 && <strong>Image 1</strong>}
    {image ? marking ? <button type="button" className="pu-button pu-image-mark-target" aria-label="Placer une croix sur la capture" onClick={markCapture}>
      <img src={image.data_url} alt={image.alt} className="pu-image-preview" /></button>
      : <>
        <button type="button" className="pu-image-zoom" title="Cliquer pour agrandir" aria-label="Agrandir l'image"
          onClick={() => zoomRef.current?.showModal()}>
          <img src={image.data_url} alt={image.alt} className="pu-image-preview" /></button>
        <dialog ref={zoomRef} className="pu-image-zoom-dialog" aria-label="Image agrandie" onClick={() => zoomRef.current?.close()}>
          <img src={image.data_url} alt={image.alt} />
        </dialog></>
      :<p className="pu-image-empty">Aucune image pour le moment.</p>}
    {image?.additional_images?.map((extra, index) => <ProductUpdateImageEditor key={index} imageLabel={`Image ${index + 2}`} image={extra} disabled={disabled || reading}
      onChange={next => {
        const { additional_images, ...primary } = image;
        replaceImage({ ...primary, additional_images: next ? [next] : undefined });
      }} />)}
    {onGenerate && <fieldset className="pu-image-selection" disabled={disabled || reading}>
      <legend>Nouveautés à illustrer</legend>
      <small>Choisissez à quelles nouveautés retenues l'image se rattache. Son contenu vient uniquement des consignes.{!allowPerItem && " Toutes cochées : l'image s'affiche en haut de la publication. Une partie seulement : à côté de la première nouveauté cochée."}</small>
      <div className="pu-image-selection-list">{notices.map(item => <label className="pu-image-selection-row" key={item.item_id}>
        <input type="checkbox" aria-label={`Illustrer ${item.title}`} checked={imageItemIds.includes(item.item_id)}
          onChange={event => setImageSelection(event.target.checked ? [...imageItemIds, item.item_id] : imageItemIds.filter(id => id !== item.item_id))} />
        <span>{item.title}</span>
      </label>)}</div>
      <small>{notices.length ? `${imageItemIds.length} / ${notices.length} nouveauté(s) pour l’image`
        : "Cochez d’abord les nouveautés à inclure dans la publication."}</small>
    </fieldset>}
    {onGenerate && codexLocalAvailable && <ProductUpdatesCodexSettings busy={disabled || reading} scope="image" engines={engines} onChange={setOptions} />}
    {onGenerate && <label>Images à créer<select aria-label="Images à créer" value={mode} disabled={disabled || reading} onChange={event => setMode(event.target.value)}>
      <option value="common">Une image commune pour les nouveautés cochées</option>
      <option value="two">Deux images communes avec des consignes séparées</option>
      {allowPerItem && <option value="per_item">Une image pour chaque nouveauté cochée</option>}
    </select></label>}
    {onGenerate && codexLocalAvailable && <label>Type de visuel<select aria-label="Type de visuel" value={imageSource} disabled={disabled || reading} onChange={event => setImageSource(event.target.value)}>
      <option value="saas_capture">Capture automatique du SaaS</option>
      <option value="illustration">Illustration générale</option>
    </select></label>}
    {onGenerate && <>{instructionFields.map(({ key, label }, index) => <label key={key}>{label}
      <textarea aria-label={label} required rows={3} maxLength={2000} value={instructions[key] || ""} disabled={disabled || reading} aria-describedby={instructionsHelpId}
        placeholder={index ? "Ex. : dans le Dispatch, faire un clic droit sur un camion du Gantt et cadrer sur Modifier chauffeur, Changer de camion et Changer de remorque."
          : "Ex. : dans Data Manager, montrer les onglets Camions, Remorques et Chauffeurs et cadrer le tableau."}
        onChange={event => setInstructions(current => ({ ...current, [key]: event.target.value }))} onPaste={event => event.stopPropagation()} />
    </label>)}<small id={instructionsHelpId}>Obligatoire. Décrivez l'écran, les clics à faire, la zone à cadrer et les éléments à encadrer. L'image suit uniquement ces consignes, pas les textes des nouveautés.</small></>}
    <div className="pu-actions">
      <button type="button" className="pu-button" disabled={disabled || reading} onClick={readClipboard}>{image?.additional_images?.length ? "Coller l’image 1" : "Coller une image"}</button>
      {onGenerate && codexLocalAvailable && imageButtons.map(index => <button key={index} type="button" className="pu-button" disabled={Boolean(reasonFor(index))} title={reasonFor(index) || undefined} onClick={() => generate("codex_local", index)}>{index
        ? `${imageSource === "saas_capture" ? "Créer la capture" : "Générer l’image"} ${index} avec ${localName}`
        : perItem ? imageSource === "saas_capture" ? `Créer ${imageItemIds.length} capture(s) du SaaS avec ${localName}` : `Générer ${imageItemIds.length} image(s) avec ${localName}`
        : imageSource === "saas_capture" ? `Créer la capture du SaaS avec ${localName}` : image ? `Régénérer l’image avec ${localName}` : `Générer l’image avec ${localName}`}</button>)}
      {image && <>
        <button type="button" className={`pu-button${marking ? " pu-button--primary" : ""}`} disabled={disabled || reading} aria-pressed={marking} onClick={() => setMarking(value => !value)}>{image.additional_images?.length ? "Ajouter une croix sur l’image 1" : "Ajouter une croix"}</button>
        {beforeMark && <button type="button" className="pu-button" disabled={disabled || reading} onClick={() => replaceImage(beforeMark)}>Annuler les croix</button>}
        <button type="button" className="pu-button pu-button--danger" disabled={disabled || reading} onClick={() => replaceImage(null)}>{image.additional_images?.length ? "Retirer l’image 1" : "Retirer l'image"}</button>
      </>}
    </div>
    {onGenerate && codexLocalAvailable && <small>{imageSource === "saas_capture"
      ? `${localName} suit vos consignes dans le SaaS local en lecture seule : il ouvre l’écran, fait les clics demandés, cadre l’image puis vérifie l’aperçu. Les données identifiantes sont masquées. Comptez environ une minute par capture et vérifiez-la avant de diffuser.`
      : localName === "Claude"
        ? "Claude dessine une illustration graphique (vectorielle, pas une photo). Le modèle se choisit dans « IA pour les visuels ». Cette action utilise votre abonnement Claude, sans crédit API."
        : `Codex crée une illustration générale. Le modèle se choisit dans « ${engines.length > 1 ? "IA" : "Codex"} pour les visuels ». Cette action utilise vos limites Codex.`}
      {perItem && " Chaque image s’affiche sous sa nouveauté."}</small>}
    {marking && <small>Cliquez sur l'action remplacée dans la capture pour y placer une croix. Au clavier, Entrée place la croix au centre.</small>}
    {onGenerate && imageSource === "illustration" && <details className="pu-image-optional"><summary>Illustration IA par API (facultative)</summary>
      <p>Si une capture ne convient pas, vous pouvez générer une illustration. Cette option utilise les crédits de l'API configurée.</p>
      {imageButtons.map(index => <button key={index} type="button" className="pu-button" disabled={Boolean(reasonFor(index, false))} title={reasonFor(index, false) || undefined}
        onClick={() => generate("configured", index)}>{index ? `Générer l’image ${index}` : image ? "Régénérer l'image" : "Générer une image"}</button>)}
    </details>}
    {reading && <small role="status">Lecture de l'image…</small>}
    {pending && <small>{autoSave ? "Changements non enregistrés : ils seront enregistrés dans le brouillon (sans le publier) à la création de l’image." : "Enregistrez vos changements pour conserver cette image."}</small>}
    {error && <p role="alert" className="pu-error">{error}</p>}
  </div>;
}
